import PropTypes from "prop-types";
import React, { useState } from "react";
import axios from "axios";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import {
  websiteRedirectDisabled,
  websiteRedirectDescription,
  websiteRedirectSuccessMessage,
} from "../../../../shared/websiteRedirectToggle";

const toastOpts = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 5000,
  hideProgressBar: true,
};

const onCopy = () => toast.success("Copied to clipboard", toastOpts);

const CopyButton = ({ url, label }) => (
  <CopyToClipboard options={{ format: 'text/plain' }} text={url} onCopy={onCopy}>
    <button type="button" className="d2-links-copy" aria-label={`Copy ${label} link`}>
      <i className="far fa-copy" />
    </button>
  </CopyToClipboard>
);

CopyButton.propTypes = { url: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

const LinkRow = ({ label, url, badge }) => (
  <div className="d2-links-row">
    <div className="d2-links-row-label">
      {label}
      {badge}
    </div>
    <div className="d2-links-row-value">
      <a className="d2-links-url" href={url} target="_blank" rel="noreferrer">{url}</a>
      <CopyButton url={url} label={label} />
    </div>
  </div>
);

LinkRow.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  badge: PropTypes.node,
};

const WebsiteRow = ({ website, links, teamId, confirmId, csrfToken, onWebsiteChange }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(website || "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setValue(website || ""); setEditing(true); };

  const save = () => {
    if (saving) return;
    setSaving(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios
      .patch(`/teams/${teamId}/confirms/${confirmId}`, {
        confirm: { venue_permalink: value, user_modified_venue_permalink: value.length > 0 },
      })
      .then(() => {
        onWebsiteChange(value.length > 0 ? value : null);
        setEditing(false);
        toast.success("Website updated", toastOpts);
      })
      .catch((e) => {
        const msg = e.response && e.response.data ? [].concat(e.response.data).join(", ") : "Something went wrong";
        toast.error(msg, toastOpts);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="d2-links-row">
      <div className="d2-links-row-label">
        Website
        {links.canEdit && !editing && (
          <button type="button" className="d2-links-edit" onClick={startEdit} aria-label={website ? "Edit website" : "Add website"}>
            <i className={website ? "fas fa-pencil" : "fas fa-plus"} />
          </button>
        )}
      </div>

      {editing ? (
        <div className="d2-links-edit-form">
          <input
            type="text"
            className="d2-links-input"
            value={value}
            placeholder="https://…"
            autoFocus
            onChange={(e) => setValue(e.target.value)}
          />
          <div className="d2-links-edit-actions">
            <button type="button" className="d2-links-cancel" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="d2-links-save" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : website ? (
        <div className="d2-links-row-value">
          <a className="d2-links-url" href={website} target="_blank" rel="noreferrer">{website}</a>
          <CopyButton url={website} label="Website" />
        </div>
      ) : null}
    </div>
  );
};

WebsiteRow.propTypes = {
  website: PropTypes.string,
  links: PropTypes.object.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  csrfToken: PropTypes.string,
  onWebsiteChange: PropTypes.func.isRequired,
};

const RedirectToggle = ({ website, enabled, teamId, confirmId, csrfToken }) => {
  const [redirecting, setRedirecting] = useState(enabled ?? false);
  const [saving, setSaving] = useState(false);
  const toggleState = { hasWebsite: !!website };

  const toggleRedirect = () => {
    if (saving) return;
    const next = !redirecting;
    setSaving(true);
    setRedirecting(next);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios
      .patch(`/teams/${teamId}/confirms/${confirmId}`, {
        confirm: { website_redirect_enabled: next },
      })
      .then(() => {
        toast.success(websiteRedirectSuccessMessage(next), toastOpts);
      })
      .catch((e) => {
        setRedirecting(!next);
        const msg = e.response && e.response.data ? [].concat(e.response.data).join(", ") : "Something went wrong";
        toast.error(msg, toastOpts);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="d2-links-redirect">
      <div className="d2-links-redirect-copy">
        <div className="d2-links-redirect-title">Redirect public event page</div>
        <div className="d2-links-redirect-desc">
          {websiteRedirectDescription(toggleState)}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={redirecting}
        aria-label="Redirect public event page to the website"
        className={`d2-switch${redirecting ? " is-on" : ""}`}
        onClick={toggleRedirect}
        disabled={websiteRedirectDisabled(toggleState) || saving}
      >
        <span className="d2-switch-knob" />
      </button>
    </div>
  );
};

RedirectToggle.propTypes = {
  website: PropTypes.string,
  enabled: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  csrfToken: PropTypes.string,
};

const LinksCard = ({ links, teamId, confirmId, csrfToken, onWebsiteChange }) => {
  const [website, setWebsite] = useState(links.website || null);

  const handleWebsiteChange = (next) => {
    setWebsite(next);
    if (onWebsiteChange) onWebsiteChange(next);
  };

  return (
    <div className="d2-card d2-links" style={{ padding: "20px" }}>
      <div className="d2-card-header" style={{ marginBottom: "12px" }}>
        <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--sl-text-primary)" }}>Links</span>
      </div>

      <WebsiteRow website={website} links={links} teamId={teamId} confirmId={confirmId} csrfToken={csrfToken} onWebsiteChange={handleWebsiteChange} />

      {links.canShareThisPage && links.shareThisPageUrl && (
        <LinkRow label="Share this Page" url={links.shareThisPageUrl} />
      )}

      <LinkRow
        label="Public Event Page"
        url={links.publicEventPageUrl}
        badge={links.published && (
          <i className="fas fa-check-circle" style={{ color: "var(--sl-text-success)", fontSize: "13px" }} />
        )}
      />

      {links.purchaseAddOnsUrl && <LinkRow label="Purchase Add-Ons Page" url={links.purchaseAddOnsUrl} />}
      {links.donationUrl && <LinkRow label="Make a Donation Page" url={links.donationUrl} />}

      {links.canEdit && (
        <RedirectToggle website={website} enabled={links.websiteRedirectEnabled} teamId={teamId} confirmId={confirmId} csrfToken={csrfToken} />
      )}
    </div>
  );
};

LinksCard.propTypes = {
  links: PropTypes.shape({
    website: PropTypes.string,
    websiteRedirectEnabled: PropTypes.bool,
    canEdit: PropTypes.bool,
    canShareThisPage: PropTypes.bool,
    shareThisPageUrl: PropTypes.string,
    publicEventPageUrl: PropTypes.string,
    published: PropTypes.bool,
    purchaseAddOnsUrl: PropTypes.string,
    donationUrl: PropTypes.string,
  }).isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  csrfToken: PropTypes.string,
  onWebsiteChange: PropTypes.func,
};

export default LinksCard;
